import dayjs from 'dayjs';
import { Address, KennoUserState } from './context/kennoTenantContext';
import IBAN from 'iban';

export const isLoggedIn = (user: KennoUserState) => {
  return user && user.isLoggedIn && user.isLoaded;
};

export const depositCategories = [218, 223, 245, 260, 318];

export const checkPhone = (phone: string) => {
  if (!/^\+?[0-9\s]+$/.test(phone)) return false;
  return !(phone.length < 7 || phone.length > 14);
};

export const activeElements =
  'input:not(.modal-input), button:not(.modal-input), a:not(.modal-link), textarea, select, #mainlocalizations';

export const modalFocusableElements = '.modal-input';

export const setAttributes = (element: Element, attrs: Record<string, any>) => {
  for (const key in attrs) {
    element.setAttribute(key, attrs[key]);
  }
};

export const removeAttributes = (element: Element, attrs: Record<string, any>) => {
  for (const key in attrs) {
    element.removeAttribute(key);
  }
};

export const formatMoney = (num?: string, nullString?: string) => {
  if (num) return parseFloat(num).toLocaleString('fi-FI', { style: 'currency', currency: 'EUR' });
  else return nullString;
};

export const formatDate = (dateText?: string | null, nullString?: string) => {
  if (dateText) {
    return new Date(Date.parse(dateText.toString())).toLocaleDateString('fi-FI');
  } else {
    return nullString;
  }
};

export const formatDateTimeFromString = (date: string, showWeekDay?: boolean, time?: string) => {
  dayjs.locale(sessionStorage.getItem('currentLanguage') ?? 'fi');
  const formattedDate = showWeekDay
    ? dayjs(date)
        .format('ddd D.M.YYYY')
        .replace(/^\w/, (c) => c.toUpperCase())
    : dayjs(date).format('D.M.YYYY');

  return time ? `${formattedDate}, ${time}` : formattedDate;
};

export const formatDateTime = (date: Date, showWeekDay?: boolean) => {
  dayjs.locale(sessionStorage.getItem('currentLanguage') ?? 'fi');
  if (showWeekDay) {
    return dayjs(date)
      .format('ddd D.M.YYYY, HH:mm')
      .replace(/^\w/, (c) => c.toUpperCase());
  }
  return dayjs(date).format('D.M.YYYY, HH:mm');
};

export const scrollToTop = () => {
  setTimeout(() => {
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
  }, 0);
};

export const capitalizeFirstLetter = (text: string | undefined): string => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const formatAddress = (address: Address) => {
  return `${address.streetAddressLine1 + (address.streetAddressLine2 || '')}, ${
    address.postalCode
  } ${capitalizeFirstLetter(address.city)}`;
};

export const scrollToElement = (elementId: string) => {
  const element = document.getElementById(elementId);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth' });
  }
};

export const htmlDecode = (text: string) =>
  new DOMParser().parseFromString(text, 'text/html').documentElement.textContent;

export const replaceDotWithComma = (text?: string | null) => {
  if (!text) return '';
  return text.replace('.', ',');
};

export function formatEuros(amount: number, showDecimals = true) {
  let sign = '';

  if (amount < 0) {
    sign = '-';
    amount = -amount;
  }

  amount = Math.round(amount * 100);
  const cents = amount % 100;
  const euros = (amount - cents) / 100;

  if (showDecimals) return sign + euros + ',' + (cents + 100 + '').substr(1) + ' €';

  return sign + euros + ' €';
}

export const formatPrice = (price?: number) => {
  if (price === undefined) {
    return '-';
  }
  return price % 1 === 0 ? formatEuros(Math.abs(price), false) : formatEuros(Math.abs(price));
};

export const shouldHideFinancialInfo = () => process.env.REACT_APP_DISABLE_FINANCIAL_INFO === '1';

export const isOlderThanOneWeek = (date: string) => {
  const dateToCompare = new Date(date);
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  weekAgo.setHours(0, 0, 0, 0); // Ignore time for an accurate full-day comparison
  return dateToCompare < weekAgo;
};

export const getContractEndDates = () => {
  const possibleEndDates = [];

  for (let i = 1; i <= 6; i++) {
    const oneMonthFromToday = dayjs().add(i, 'month');
    const lastDay = oneMonthFromToday.endOf('month');
    possibleEndDates.push({ label: lastDay.toDate().toLocaleDateString(), value: lastDay.format('YYYY-MM-DD') });
  }

  return possibleEndDates;
};

export const isFinnishIBAN = (iban: string) => {
  return IBAN.isValid(iban) && iban.substring(0, 2).toUpperCase() === 'FI';
};

export const isForeignIBAN = (iban: string) => {
  return IBAN.isValid(iban) && iban.substring(0, 2).toUpperCase() !== 'FI';
};
